import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Checkbox,
  FormControlLabel,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import api from '../../services/api';
import { validateNoLinks } from '../../utils/validation';
import { useTranslation } from 'react-i18next';

interface Department {
  id: string;
  name: string;
  notes?: string;
  materials?: Array<{ id: string; name: string; unit: string }>;
}

const DepartmentsManagement: React.FC = () => {
  const { t } = useTranslation();
  const [departments, setDepartments] = useState<Department[]>([]);
  const [materials, setMaterials] = useState<any[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState<Department | null>(null);
  const [formData, setFormData] = useState({ name: '', notes: '', materialIds: [] as string[] });
  const [notesError, setNotesError] = useState<string>('');

  useEffect(() => {
    loadDepartments();
    loadMaterials();
  }, []);

  const loadDepartments = async () => {
    try {
      const res = await api.get('/reference/departments');
      // Load materials count for each department
      const departmentsWithMaterials = await Promise.all(
        res.data.map(async (dept: Department) => {
          try {
            const deptRes = await api.get(`/reference/departments/${dept.id}`);
            return { ...dept, materials: deptRes.data.materials || [] };
          } catch {
            return { ...dept, materials: [] };
          }
        })
      );
      setDepartments(departmentsWithMaterials);
    } catch (err) {
      console.error('Failed to load departments:', err);
    }
  };

  const loadMaterials = async () => {
    try {
      const res = await api.get('/reference/materials');
      setMaterials(res.data);
    } catch (err) {
      console.error('Failed to load materials:', err);
    }
  };

  const handleOpenDialog = async (department?: Department) => {
    if (department) {
      const deptRes = await api.get(`/reference/departments/${department.id}`);
      setEditingDepartment(deptRes.data);
      setFormData({
        name: deptRes.data.name,
        notes: deptRes.data.notes || '',
        materialIds: deptRes.data.materials?.map((m: any) => m.id) || [],
      });
    } else {
      setEditingDepartment(null);
      setFormData({ name: '', notes: '', materialIds: [] });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    // Validate notes for links
    const notesValidation = validateNoLinks(formData.notes);
    if (!notesValidation.isValid) {
      setNotesError(notesValidation.error || '');
      return;
    }
    setNotesError('');

    try {
      if (editingDepartment) {
        await api.put(`/reference/departments/${editingDepartment.id}`, formData);
      } else {
        await api.post('/reference/departments', formData);
      }
      setDialogOpen(false);
      loadDepartments();
    } catch (err) {
      console.error('Failed to save department:', err);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('admin.deleteDepartmentConfirm'))) return;
    try {
      await api.delete(`/reference/departments/${id}`);
      loadDepartments();
    } catch (err) {
      console.error('Failed to delete department:', err);
    }
  };

  return (
    <Box>
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={() => handleOpenDialog()}
        sx={{ mb: 2 }}
      >
        {t('admin.addDepartment')}
      </Button>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('common.name')}</TableCell>
              <TableCell>{t('admin.notes')}</TableCell>
              <TableCell>{t('admin.materials')}</TableCell>
              <TableCell>{t('common.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {departments.map((dept: Department) => (
              <TableRow key={dept.id}>
                <TableCell>{dept.name}</TableCell>
                <TableCell>{dept.notes || '-'}</TableCell>
                <TableCell>
                  {dept.materials && dept.materials.length > 0 ? (
                    <Typography variant="body2">
                      {dept.materials.length} {t('admin.materialsCount')}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {t('admin.noMaterials')}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => handleOpenDialog(dept)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDelete(dept.id)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingDepartment ? t('admin.editDepartment') : t('admin.addDepartment')}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label={t('common.name')}
            value={formData.name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, name: e.target.value })}
            sx={{ mt: 2 }}
          />
          <TextField
            fullWidth
            multiline
            rows={4}
            label={t('admin.notes')}
            value={formData.notes}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setFormData({ ...formData, notes: e.target.value });
              const validation = validateNoLinks(e.target.value);
              setNotesError(validation.error || '');
            }}
            error={!!notesError}
            helperText={notesError || t('admin.linksNotAllowed')}
            sx={{ mt: 2 }}
          />
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              {t('admin.assignMaterialsToDepartment')}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              {t('admin.assignMaterialsDescription')}
            </Typography>
            <Box sx={{ maxHeight: 300, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
              {materials.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                  {t('admin.noMaterialsAvailableCreate')}
                </Typography>
              ) : (
                materials.map((material: any) => (
                  <FormControlLabel
                    key={material.id}
                    control={
                      <Checkbox
                        checked={formData.materialIds.includes(material.id)}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          if (e.target.checked) {
                            setFormData({
                              ...formData,
                              materialIds: [...formData.materialIds, material.id],
                            });
                          } else {
                            setFormData({
                              ...formData,
                              materialIds: formData.materialIds.filter((id: string) => id !== material.id),
                            });
                          }
                        }}
                      />
                    }
                    label={`${material.name} (${material.unit})${material.is_active === false ? ` ${t('admin.inactiveLabel')}` : ''}`}
                    sx={{
                      display: 'block',
                      mb: 0.5,
                      '& .MuiFormControlLabel-label': {
                        fontSize: '0.875rem',
                      },
                    }}
                  />
                ))
              )}
            </Box>
            {formData.materialIds.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {formData.materialIds.length} {t('admin.materialsSelected')}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleSubmit} variant="contained">
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DepartmentsManagement;

